import React, { useContext, useEffect, useState, useRef } from 'react';
import AdminContext from '../../../Context/Admin/adminContext';
import '../UserManagement/UserManagement.css';
import './SessionManagement.css';

const SessionManagement = () => {
  const adminContext = useContext(AdminContext);
  const {
    activeSessions,
    sessionStats,
    getAllSessions,
    revokeSession,
    cleanupExpiredTokens,
  } = adminContext;
  const [filterText, setFilterText] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const hasFetchedInitial = useRef(false);

  useEffect(() => {
    // Fetch sessions only once on initial load
    if (getAllSessions && !hasFetchedInitial.current) {
      hasFetchedInitial.current = true;
      getAllSessions();
    }

    // Setup auto-refresh interval
    let interval;
    if (autoRefresh && getAllSessions) {
      interval = setInterval(() => {
        getAllSessions();
      }, 30000); // Refresh every 30 seconds
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoRefresh, getAllSessions]);

  const handleRevoke = async (sessionId, username) => {
    if (
      !window.confirm(
        `Revoke session for ${username}? They will be logged out.`
      )
    ) {
      return;
    }

    if (revokeSession) {
      const result = await revokeSession(sessionId);
      if (result?.success) {
        alert(`Session revoked for ${username}`);
      }
    }
  };

  const handleCleanup = async () => {
    if (!window.confirm('Clean up all expired tokens?')) {
      return;
    }

    if (cleanupExpiredTokens) {
      const result = await cleanupExpiredTokens();
      if (result?.success) {
        alert(
          `Cleanup complete! Deleted ${result.deletedCount || 0} expired tokens`
        );
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  const getTimeUntilExpiry = (expiresAt) => {
    if (!expiresAt) return 'Unknown';
    const now = new Date();
    const expiry = new Date(expiresAt);
    const diff = expiry.getTime() - now.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h`;
    return 'Soon';
  };

  const filteredSessions = (activeSessions || []).filter((session) => {
    const searchLower = filterText.toLowerCase();
    return (
      session.username?.toLowerCase().includes(searchLower) ||
      session.ipAddress?.includes(searchLower) ||
      session.browser?.toLowerCase().includes(searchLower)
    );
  });

  return (
    <div className="user-management">
      <div className="management-header">
        <h1>Session Management</h1>
        <p className="management-subtitle">
          Monitor and manage active user sessions
        </p>
      </div>

      {/* Session Statistics */}
      {sessionStats && (
        <div className="metrics-grid" style={{ marginBottom: '24px' }}>
          <div className="metric-card primary">
            <div className="metric-icon">🔒</div>
            <div className="metric-content">
              <div className="metric-value">
                {sessionStats.totalActiveSessions || 0}
              </div>
              <div className="metric-label">Active Sessions</div>
            </div>
          </div>

          <div className="metric-card success">
            <div className="metric-icon">👥</div>
            <div className="metric-content">
              <div className="metric-value">
                {sessionStats.uniqueUsers || 0}
              </div>
              <div className="metric-label">Unique Users</div>
            </div>
          </div>
        </div>
      )}

      {/* Controls */}
      <div className="section-card">
        <div className="session-controls">
          <div className="control-group">
            <input
              type="text"
              placeholder="Filter by username or IP..."
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              className="filter-input"
            />
            <span className="session-count">
              {filteredSessions.length} sessions
            </span>
          </div>
          <div className="control-buttons">
            <label className="auto-refresh-toggle">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
              />
              <span>Auto-refresh</span>
            </label>
            <button
              className="btn-primary"
              onClick={() => getAllSessions && getAllSessions()}
            >
              ↻ Refresh
            </button>
            <button className="btn-cleanup" onClick={handleCleanup}>
              🧹 Cleanup
            </button>
          </div>
        </div>
      </div>

      {/* Sessions Table */}
      <div className="section-card">
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Device Info</th>
                <th>IP Address</th>
                <th>Created</th>
                <th>Last Used</th>
                <th>Expires</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredSessions.length === 0 ? (
                <tr>
                  <td
                    colSpan="7"
                    style={{
                      textAlign: 'center',
                      padding: '40px',
                      color: '#718096',
                    }}
                  >
                    No active sessions found
                  </td>
                </tr>
              ) : (
                filteredSessions.map((session, index) => (
                  <tr key={index}>
                    <td>
                      <div>
                        <span className="username-badge">
                          {session.username}
                        </span>
                        {session.current && (
                          <span className="current-badge">Current</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="device-info">
                        <div>
                          <strong>{session.browser}</strong>
                        </div>
                        <div style={{ fontSize: '12px', color: '#718096' }}>
                          {session.operatingSystem}
                        </div>
                      </div>
                    </td>
                    <td>{session.ipAddress}</td>
                    <td style={{ fontSize: '13px' }}>
                      {formatDate(session.createdAt)}
                    </td>
                    <td style={{ fontSize: '13px' }}>
                      {formatDate(session.lastUsedAt)}
                    </td>
                    <td>
                      <span className="expires-badge">
                        {getTimeUntilExpiry(session.expiresAt)}
                      </span>
                    </td>
                    <td>
                      <button
                        className="btn-revoke"
                        onClick={() =>
                          handleRevoke(session.id, session.username)
                        }
                        disabled={session.current}
                      >
                        Revoke
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SessionManagement;
